<script setup lang="ts">
import type { ReportTemplateId } from '@src/components/report-generator/report-reusables/reportTemplates';

import type { CSSProperties } from 'vue';

import { useUpdatedReportStore } from '@src/store/report-store';
import { computed, onMounted, provide, ref } from 'vue';

import ReportPanelRenderer from './report-reusables/ReportPanelRenderer.vue';

const props = defineProps<{
  templateId?: ReportTemplateId;
}>();

const reportStore = useUpdatedReportStore();
const reportElement = ref<HTMLDivElement | null>(null);

const loadedTemplate = computed(() => {
  if (props.templateId) {
    return reportStore.getTemplateForPage(props.templateId);
  }
  return reportStore.getTemplate();
});

const reportPanels = computed(() => {
  if (props.templateId) {
    return reportStore.getPanelsForPage(props.templateId) || [];
  }
  return reportStore.getPanels() || [];
});

const templateDims = computed(() => {
  if (props.templateId) {
    return reportStore.getTemplateDimsForPage(props.templateId);
  }
  return reportStore.getTemplateDims();
});

// Provide the theme globally so all descendants can inject it
provide(
  'reportTheme',
  computed(() => loadedTemplate.value?.theme ?? null),
);

// Grid style: uses PageLayout.columnGap between panels
const panelsGridStyle = computed<CSSProperties>(() => {
  const layout = loadedTemplate.value?.layout;
  if (!layout) return {};
  return {
    gap: `${layout.columnGap}px`,
    padding: `${layout.margin}px`,
  };
});

// Report outer dims from template size
const reportDimStyle = computed<CSSProperties>(() => {
  const { width, height, unit } = templateDims.value;
  if (!width || !height) return {};
  return {
    width: `${width}${unit}`,
    height: `${height}${unit}`,
  };
});

onMounted(() => {
  // Template is auto-loaded via ensureDefaultPageLoaded inside getPanels/getTemplate
});
</script>

<template>
  <div ref="reportElement" class="report-element" :style="reportDimStyle">
    <div class="report-panels" :style="panelsGridStyle">
      <ReportPanelRenderer
        v-for="panel in reportPanels"
        :key="panel.panelId"
        :panel="panel"
        :style="{ flex: `${panel.width} ${panel.width} 0%`, minWidth: 0 }"
      />
    </div>
  </div>
</template>

<style scoped>
* {
  user-select: none;
}

.report-element {
  overflow: hidden;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  flex-direction: column;
  border: 2px solid black;
  border-radius: 12px;
  background: #ffffff;
  box-sizing: border-box;
}

.report-panels {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}
</style>
